import Env from "@/core/config/env";
import { ApiResponseError } from "@/core/exceptions/exception-adapter";
import { repositoryClient, toRepositoryBody } from "@/core/network/repository-client";
import {
  type AuthEntryPoint,
  type AuthFlashError,
  type AuthMeResponse,
  type EntryIntentResponse,
  type LogoutResponse,
} from "@/core/auth/types";

function toAbsoluteApiUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  return Env.apiEndpoint(pathOrUrl);
}

export async function startAuthIntent(
  entryPoint: AuthEntryPoint,
  returnTo?: string,
): Promise<EntryIntentResponse> {
  return repositoryClient.post<EntryIntentResponse>("/auth/entry-intent", {
    body: toRepositoryBody({
      entryPoint,
      returnTo,
    }),
  });
}

export async function getCurrentUser(): Promise<AuthMeResponse | null> {
  try {
    return await repositoryClient.get<AuthMeResponse>("/auth/me");
  } catch (error) {
    if (error instanceof ApiResponseError && error.status === 401) {
      return null;
    }

    throw error;
  }
}

export async function getAuthFlashError(): Promise<AuthFlashError | null> {
  const response = await repositoryClient.get<AuthFlashError | null | "">("/auth/flash-error");

  if (!response || typeof response === "string") {
    return null;
  }

  return response;
}

export async function logout(returnTo?: string): Promise<LogoutResponse> {
  try {
    return await repositoryClient.post<LogoutResponse>("/auth/logout", {
      body: toRepositoryBody({
        returnTo,
      }),
    });
  } catch (error) {
    if (error instanceof ApiResponseError && error.status === 401) {
      return {
        success: true,
        logoutUrl: returnTo ?? "/",
      };
    }

    throw error;
  }
}

export { toAbsoluteApiUrl };
